import { z } from 'zod';
export const API_PREFIX = '/api/v1';
export const IDEMPOTENCY_HEADER = 'idempotency-key';
export const CONTRACT_VERSION = '1.0.0';
export const uuidSchema = z.string().uuid();
/**
 * Quantities travel over the wire as canonical decimal strings so that no binary
 * floating point arithmetic is ever involved. Examples: "1", "0.5", "2.25".
 */
export const decimalStringSchema = z
    .string()
    .regex(/^-?\d+(\.\d+)?$/, 'Expected a decimal string such as "1.5"');
export const positiveDecimalStringSchema = decimalStringSchema.refine((v) => !v.startsWith('-') && /[1-9]/.test(v), 'Expected a positive decimal');
export const nonNegativeDecimalStringSchema = decimalStringSchema.refine((v) => !v.startsWith('-'), 'Expected a non-negative decimal');
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** An absolute http(s) link as a user types it (a video, a website); nothing else is a link here. */
export const httpUrlSchema = z
    .string()
    .trim()
    .max(2000)
    .url()
    .refine((v) => /^https?:\/\//i.test(v), 'Expected an http(s) URL');
// Languages live in locales.ts – the one list (docs/partial/localization-spec.md 6.1).
export { DEFAULT_LOCALE, SUPPORTED_LOCALES, localeSchema } from './locales.js';
export const cursorQuerySchema = z.object({
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export function cursorPageSchema(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
        total: z.number().int().nonnegative().optional(),
    });
}
export const apiErrorCodeSchema = z.enum([
    'VALIDATION_ERROR',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'VERSION_CONFLICT',
    'RATE_LIMITED',
    'PAYLOAD_TOO_LARGE',
    'UNSUPPORTED_MEDIA_TYPE',
    'IDEMPOTENCY_MISMATCH',
    'FEATURE_DISABLED',
    'PROVIDER_UNAVAILABLE',
    'IMPORT_BLOCKED',
    'IMPORT_FAILED',
    'ACCOUNT_DEACTIVATED',
    'EMAIL_NOT_VERIFIED',
    'INVALID_CREDENTIALS',
    'TOKEN_INVALID',
    'INTERNAL_ERROR',
]);
export const apiErrorSchema = z.object({
    code: apiErrorCodeSchema,
    /** English, for logs and API users. Clients show their own text for `reason` or `code`. */
    message: z.string(),
    /** Why, finer than `code` (see error-reasons.ts); absent for most technical errors. */
    reason: z.string().optional(),
    /** Values for the placeholders of the reason's text. */
    params: z.record(z.union([z.string(), z.number()])).optional(),
    details: z.unknown().optional(),
    traceId: z.string().optional(),
});
/** Payload delivered with a 409 VERSION_CONFLICT so the UI can offer reload or merge. */
export const versionConflictDetailsSchema = z.object({
    expectedVersion: z.number().int(),
    currentVersion: z.number().int(),
    current: z.unknown(),
});
export const okResponseSchema = z.object({ ok: z.literal(true) });
export const idResponseSchema = z.object({ id: uuidSchema });
export const localizedTextSchema = z.object({ de: z.string(), en: z.string() });
export const permissionsSchema = z.object({
    canView: z.boolean(),
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    canShare: z.boolean(),
});
/** Query-string boolean: "true"/"1"/"yes" → true, "false"/"0"/"no"/"" → false. */
export const queryBooleanSchema = z.preprocess((v) => {
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'string')
        return ['true', '1', 'yes', 'on'].includes(v.toLowerCase());
    return v;
}, z.boolean());
//# sourceMappingURL=common.js.map